import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { Wand2, Sparkles, Image as ImageIcon, Download } from 'lucide-react';
import { Maximize2, Video } from 'lucide-react';
import Spinner from './components/Spinner';
import StartScreen from './components/StartScreen';
import Sidebar from './components/Sidebar';
import VideosQualityDashboard from './components/VideosQualityDashboard';
import FeedbackPanel from './components/FeedbackPanel';
import FloatingActionCluster from './components/FloatingActionCluster';
import ChatWidget from './components/ChatWidget';
import ImageGalleryModal from './components/ImageGalleryModal';
import ActiveJobsIndicator from './components/ActiveJobsIndicator';
import { VideoTab, videoTabIcons } from './types';
import EditorCanvas from './components/EditorCanvas';
import PanelArea from './components/PanelArea';
import Toolbar from './components/Toolbar';
import BrandGuidelinesToggle from './components/BrandGuidelinesToggle';
import BrandGuidelinesGateModal from './components/BrandGuidelinesGateModal';

import { Comment } from 'hooks/useCommentsRealtime';

// Context imports
import { useSettings } from './contexts/SettingsContext';
import { useThread } from './contexts/ThreadContext';
import { useFeedback } from './contexts/FeedbackContext';
import { useVideo } from './contexts/VideoContext';
import { useEditor } from './contexts/EditorContext';

export interface ViewProps {
  uid: string;
  hasAccessToBulkTreatments: boolean;
  hasAccessToAiChat: boolean;
}

const LandingPageView: React.FC<ViewProps> = ({ uid, hasAccessToBulkTreatments, hasAccessToAiChat }) => {
  const navigate = useNavigate();

  // Pull all values from contexts
  const { isExpanded, setIsExpanded } = useSettings();
  const { currentProjectId, currentThreadId, currentVideoId, selectProjectIdAndThreadId, onThreadSelect } = useThread();
  const {
    isAnnotationMode, pendingAnnotation,
    isFeedbackPanelExpanded, setIsFeedbackPanelExpanded,
    replyingTo, setReplyingTo, comments, activeUsers, currentUserEmail,
    handleAddComment, handleHighlightAnnotation, handleClearAnnotation,
    deleteComment, toggleReaction, toggleResolve,
    setPendingFeedbackForChat,
  } = useFeedback();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasChatConversation, setHasChatConversation] = useState(false);
  const {
    editorMode, selectedVideo, activeVideoTab, setActiveVideoTab,
    videoEditPrompt, setVideoEditPrompt,
    videoRef,
    isVideoLoading, isSubmitting: isVideoSubmitting,
    videoHistory, videosLoading,
    handleEditVideo, handleExtendVideo,
    handleExitVideoMode, handleDownloadVideo,
  } = useVideo();
  const {
    historyIndex, selectedImageInGroup,
    isLoading, loadingMessage, error, setError,
    isImageGalleryOpen,
    imageGroups, imagesLoading, currentImage, currentImageGroup,
    totalImageCount, isVideoRoute,
    selectedProjectName, userProjects, projectsLoading,
    handleFileSelect, handlePromptSubmit,
    handleOpenGallery, handleCloseGallery, handleNavigateToImage,
    handleGalleryDownload, handleToggleStar, handleDeletePicture,
    onThreadDelete, onThreadPatch, onThreadCreate,
    activeJobs,
  } = useEditor();

  // Determine current picture ID for feedback system
  const currentPictureId = editorMode === 'video'
    ? selectedVideo?.video_id
    : currentImage?.picture_id;

  const showAskAI = hasAccessToAiChat;

  const thumbnailMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const group of imageGroups) {
      for (const img of group.images) {
        if (img.thumb_url || img.output_url) {
          map[img.picture_id] = img.thumb_url || img.output_url;
        }
      }
    }
    return map;
  }, [imageGroups]);

  const getImageUrl = useCallback((pictureId: string): string | null => {
    for (const group of imageGroups) {
      for (const img of group.images) {
        if (img.picture_id === pictureId) return img.output_url
      }
    }
    return null
  }, [imageGroups]);

  const handleAskAI = useCallback((comment: Comment, replies: Comment[]) => {
    const savedPosition = { historyIndex, selectedImageInGroup };
    // Navigate to the comment's picture so context is visible
    if (comment.picture_id) {
      const groupIndex = imageGroups.findIndex(g => g.images.some(img => img.picture_id === comment.picture_id));
      if (groupIndex >= 0) {
        const imageIndex = imageGroups[groupIndex].images.findIndex(img => img.picture_id === comment.picture_id);
        handleNavigateToImage(groupIndex, imageIndex >= 0 ? imageIndex : undefined);
      }
    }
    const imageUrl = comment.picture_id ? getImageUrl(comment.picture_id) : null;
    setPendingFeedbackForChat({
      comment,
      replies,
      imageUrl,
      coordinates: comment.coordinates || null,
      savedPosition,
    });
  }, [imageGroups, historyIndex, selectedImageInGroup, handleNavigateToImage, getImageUrl, setPendingFeedbackForChat]);

  const handleCancelAskAI = useCallback((savedPosition?: { historyIndex: number; selectedImageInGroup: number }) => {
    setIsChatOpen(false);
    setIsFeedbackPanelExpanded(true);
    if (savedPosition) {
      handleNavigateToImage(savedPosition.historyIndex, savedPosition.selectedImageInGroup >= 0 ? savedPosition.selectedImageInGroup : undefined);
    }
  }, [handleNavigateToImage, setIsFeedbackPanelExpanded]);

  // Build sets of known image/video IDs to filter comments by mode
  const imagePictureIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of imageGroups) {
      for (const img of group.images) {
        ids.add(img.picture_id);
      }
    }
    return ids;
  }, [imageGroups]);

  const videoPictureIds = useMemo(() => {
    const ids = new Set<string>();
    for (const video of videoHistory) {
      ids.add(video.video_id);
    }
    return ids;
  }, [videoHistory]);

  // Filter comments so video comments don't appear in picture mode and vice versa
  const pictureComments = useMemo(() =>
    comments.filter(c => !c.picture_id || c.parent_comment_id || imagePictureIds.has(c.picture_id)),
    [comments, imagePictureIds]
  );

  const videoComments = useMemo(() =>
    comments.filter(c => !c.picture_id || c.parent_comment_id || videoPictureIds.has(c.picture_id)),
    [comments, videoPictureIds]
  );

  const renderContent = () => {
      if (error) {
         return (
             <div className="text-center animate-fade-in bg-red-500/10 border border-red-500/20 p-8 rounded-lg max-w-2xl mx-auto flex flex-col items-center gap-4">
              <h2 className="text-2xl font-bold text-red-300">An Error Occurred</h2>
              <p className="text-md text-red-400">{error}</p>
              <button
                  onClick={() => {
                    setError(null);
                    // If we're on an invalid thread, navigate back to project or home
                    if (currentThreadId && imageGroups.length === 0) {
                      if (currentProjectId) {
                        selectProjectIdAndThreadId(currentProjectId, null);
                      } else {
                        selectProjectIdAndThreadId(null, null);
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded-lg text-md transition-colors"
                >
                  Go Back
              </button>
            </div>
          );
      }

      // Show loader when loading images/videos from Firestore
      if ((imagesLoading || videosLoading) && !currentImage) {
        return (
          <div className="text-center animate-fade-in flex flex-col items-center gap-4">
            <Spinner />
            <p className="text-gray-300 text-lg">Loading thread data...</p>
          </div>
        );
      }

      // Show loader while projects are loading (prevents glitch)
      if (projectsLoading) {
        return (
          <div className="text-center animate-fade-in flex flex-col items-center gap-4">
            <Spinner />
            <p className="text-gray-300 text-lg">Loading your projects...</p>
          </div>
        );
      }

      if (!currentImage && !currentImageGroup && editorMode === 'picture') {
        return <StartScreen
          onFileSelect={handleFileSelect}
          onPromptSubmit={handlePromptSubmit}
          isLoading={isLoading}
          currentProjectName={selectedProjectName}
          hasAnyProjects={userProjects.length > 0}
          projects={userProjects}
          onThreadCreate={onThreadCreate}
          onThreadSelect={onThreadSelect}
          onThreadEdit={onThreadPatch}
          onThreadDelete={onThreadDelete}
          hasAccessToBulkTreatments={hasAccessToBulkTreatments}
          onProjectSettings={(projectId) => navigate(generatePath("/projects/:project_id/settings", { project_id: projectId }), { replace: true })}
        />;
      }

      // VIDEO EDITING MODE
      if (editorMode === 'video' && selectedVideo) {
        return (
          <div className="w-full h-[calc(100vh-140px)] flex gap-4 animate-fade-in overflow-hidden">
            {/* Left Sidebar - Video Controls - Smoothly transitions width to 0 when feedback panel opens */}
            <div className={`flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${
              isFeedbackPanelExpanded ? 'w-0 opacity-0' : 'w-[40%] opacity-100'
            }`}>
              {/* Mode Switcher */}
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={handleExitVideoMode}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 rounded-lg text-sm text-gray-300 hover:text-white transition-colors"
                >
                  <ImageIcon className="w-4 h-4" />
                  Back to Pictures
                </button>
                <div className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg">
                  <Video className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-purple-300">Video Editing Mode</span>
                </div>
              </div>

              {/* Video Tabs - Only Editing and Extending */}
              <div className="grid grid-cols-2 gap-1 mb-4">
                {(['Editing', 'Extending'] as VideoTab[]).map(tab => {
                  const Icon = videoTabIcons[tab];
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveVideoTab(tab)}
                      className={`
                        flex items-center justify-center gap-2 capitalize font-medium py-2 px-3 rounded-lg text-sm
                        transition-colors duration-200 whitespace-nowrap
                        ${activeVideoTab === tab
                          ? 'bg-purple-600/40 text-white border border-purple-500/50'
                          : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/40 border border-transparent'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab}
                    </button>
                  );
                })}
              </div>

              {/* Tab-specific Controls - Scrollable */}
              <div className="flex-1 flex flex-col gap-3 bg-gray-800/60 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-700/50 overflow-y-auto">
                {activeVideoTab === 'Editing' && (
                  <div className="space-y-3">
                    <BrandGuidelinesToggle category="creative" />

                    <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-xl border border-purple-500/20 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Video className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-200 mb-1">Video Editing</p>
                          <p className="text-xs text-gray-400 leading-relaxed">
                            Describe the changes you want to make to your video. AI will process and apply your edits.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl p-4 shadow-lg">
                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                          <Wand2 className="w-4 h-4 text-purple-400" />
                          Your Prompt
                        </label>
                        <textarea
                          value={videoEditPrompt}
                          onChange={(e) => setVideoEditPrompt(e.target.value)}
                          placeholder='Describe the changes you want to make to this video...'
                          className="w-full bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 text-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:outline-none transition disabled:cursor-not-allowed disabled:opacity-60 resize-none placeholder:text-gray-500"
                          disabled={isVideoLoading}
                          rows={4}
                        />
                        <button
                          onClick={handleEditVideo}
                          className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-blue-600 text-white font-semibold py-3 px-4 text-sm rounded-lg transition-all duration-300 ease-out shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 relative overflow-hidden group"
                          disabled={isVideoLoading || isVideoSubmitting || !videoEditPrompt.trim()}
                        >
                          <span className="relative z-10 flex items-center justify-center gap-2">
                            {isVideoSubmitting ? (
                              <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4" />
                                Edit Video
                              </>
                            )}
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeVideoTab === 'Extending' && (
                  <div className="space-y-3">
                    <BrandGuidelinesToggle category="creative" />
                    <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-xl border border-purple-500/20 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Maximize2 className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-200 mb-1">Video Extending</p>
                          <p className="text-xs text-gray-400 leading-relaxed">
                            Extend your video by adding more content. Optionally describe what should happen.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl p-4 shadow-lg">
                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                          <Wand2 className="w-4 h-4 text-purple-400" />
                          Prompt (Optional)
                        </label>
                        <textarea
                          value={videoEditPrompt}
                          onChange={(e) => setVideoEditPrompt(e.target.value)}
                          placeholder='Describe what should happen in the extended portion...'
                          className="w-full bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 text-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:outline-none transition disabled:cursor-not-allowed disabled:opacity-60 resize-none placeholder:text-gray-500"
                          disabled={isVideoLoading}
                          rows={3}
                        />
                        <button
                          onClick={handleExtendVideo}
                          className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-blue-600 text-white font-semibold py-3 px-4 text-sm rounded-lg transition-all duration-300 ease-out shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 relative overflow-hidden group"
                          disabled={isVideoLoading || isVideoSubmitting}
                        >
                          <span className="relative z-10 flex items-center justify-center gap-2">
                            {isVideoSubmitting ? (
                              <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Maximize2 className="w-4 h-4" />
                                Extend by 7s
                              </>
                            )}
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Middle - Video Display and Action Buttons */}
            <div className="flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out w-[60%]">
              {/* Video Display */}
              <div className="relative flex-1 shadow-2xl rounded-xl overflow-hidden bg-black/40 flex items-center justify-center">
                {isVideoLoading && (
                  <div className="absolute inset-0 bg-black/70 z-30 flex flex-col items-center justify-center gap-4 animate-fade-in">
                    <Spinner />
                    <p className="text-gray-300 text-sm">{loadingMessage || 'Processing...'}</p>
                  </div>
                )}

                <video
                  ref={videoRef}
                  key={selectedVideo.video_id}
                  src={selectedVideo.output_url}
                  controls
                  className="max-w-full max-h-[calc(100vh-250px)] rounded-xl"
                >
                  Your browser does not support the video tag.
                </video>

                {/* Video info overlay */}
                <div className="absolute top-4 left-4 bg-purple-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg z-20 flex items-center gap-2">
                  <Video className="w-3 h-3" />
                  {selectedVideo.title || `Video ${selectedVideo.video_id.slice(0, 8)}`}
                </div>
              </div>

              {/* Action Buttons - No Undo/Redo for videos */}
              <div className="flex flex-wrap items-center justify-center gap-2 bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 backdrop-blur-sm">
                <button
                  onClick={handleExitVideoMode}
                  className="flex items-center justify-center bg-white/10 border border-white/20 text-gray-200 font-semibold py-2.5 px-4 rounded-md transition-all duration-200 hover:bg-white/20 hover:border-white/30 active:scale-95 text-sm"
                >
                  <ImageIcon className="w-4 h-4 mr-1.5" />
                  Back to Pictures
                </button>

                <div className="h-6 w-px bg-gray-600"></div>

                <button
                  onClick={handleDownloadVideo}
                  className="bg-gradient-to-br from-green-600 to-green-500 text-white font-bold py-2.5 px-5 rounded-md transition-all duration-300 ease-in-out shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/40 hover:-translate-y-px active:scale-95 text-sm flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Video
                </button>
              </div>
            </div>

            {/* Right Side - Feedback Panel for Video Mode */}
            <div
              className={`flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${
                isFeedbackPanelExpanded ? 'w-[40%] opacity-100' : 'w-0 opacity-0'
              }`}
            >
              <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl flex flex-col overflow-hidden">
                <FeedbackPanel
                  comments={videoComments}
                  activeUsers={activeUsers}
                  currentUserId={uid}
                  currentUserEmail={currentUserEmail || ''}
                  onAddComment={handleAddComment}
                  onDeleteComment={(commentId) => deleteComment(commentId, uid)}
                  onToggleReaction={toggleReaction}
                  onToggleResolve={toggleResolve}
                  currentPictureId={selectedVideo?.video_id}
                  isAnnotationMode={false}
                  pendingAnnotation={null}
                  onClearAnnotation={() => {}}
                  onHighlightAnnotation={() => {}}
                  onClose={() => setIsFeedbackPanelExpanded(false)}
                  replyingTo={replyingTo}
                  setReplyingTo={setReplyingTo}
                  isVideoMode={true}
                  showAskAI={showAskAI}
                  onAskAI={handleAskAI}
                />
              </div>
            </div>

            {/* Right Side - Chat Panel (docked) */}
            <div
              className={`flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${
                isChatOpen ? 'w-[40%] opacity-100' : 'w-0 opacity-0'
              }`}
            >
              <div id="chat-panel-portal" className="flex-1 flex flex-col overflow-hidden" />
            </div>
          </div>
        );
      }

      // PICTURE MODE — composed from sub-components
      return (
        <div className="w-full h-[calc(100vh-140px)] flex gap-4 animate-fade-in overflow-hidden">
          {/* Left Sidebar - Controls */}
          <PanelArea />

          {/* Middle - Image Display and Action Buttons */}
          <div
            className={'flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out w-[60%]'}
          >
            {/* Image Display */}
            <EditorCanvas currentPictureId={currentPictureId} />

            {/* Action Buttons */}
            <Toolbar />
          </div>

          {/* Right Side - Feedback Panel */}
          <div
            className={`flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${
              isFeedbackPanelExpanded ? 'w-[40%] opacity-100' : 'w-0 opacity-0'
            }`}
          >
            <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl flex flex-col overflow-hidden">
              <FeedbackPanel
                comments={pictureComments}
                activeUsers={activeUsers}
                currentUserId={uid}
                currentUserEmail={currentUserEmail || ''}
                onAddComment={handleAddComment}
                onDeleteComment={(commentId) => deleteComment(commentId, uid)}
                onToggleReaction={toggleReaction}
                onToggleResolve={toggleResolve}
                currentPictureId={currentPictureId}
                isAnnotationMode={isAnnotationMode}
                pendingAnnotation={pendingAnnotation}
                onClearAnnotation={handleClearAnnotation}
                onHighlightAnnotation={handleHighlightAnnotation}
                onClose={() => setIsFeedbackPanelExpanded(false)}
                replyingTo={replyingTo}
                setReplyingTo={setReplyingTo}
                showAskAI={showAskAI}
                onAskAI={handleAskAI}
              />
            </div>
          </div>

          {/* Right Side - Chat Panel (docked) */}
          <div
            className={`flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${
              isChatOpen ? 'w-[40%] opacity-100' : 'w-0 opacity-0'
            }`}
          >
            <div id="chat-panel-portal" className="flex-1 flex flex-col overflow-hidden" />
          </div>
        </div>
      );
  };

  return (
    <div className="text-gray-100 flex flex-col">
        {/* Show Sidebar when there's a current image OR image group (including grid view) */}
        {(currentImage || currentImageGroup) && (
          <Sidebar
            projects={userProjects}
            onToggle={() => setIsExpanded(prev => !prev)}
            isExpanded={isExpanded}
            onThreadSelect={onThreadSelect}
            selectedProjectId={currentProjectId || null}
            selectedThreadId={currentThreadId || null}
            onThreadDelete={onThreadDelete}
            onThreadEdit={onThreadPatch}
            onThreadCreate={onThreadCreate}
          />
        )}
        <main
          className={`flex-grow w-full max-w-[1600px] mx-auto p-4 md:p-8 flex justify-center transition-all duration-300 ${currentImage || currentImageGroup ? 'lg:pl-20' : ''} ${currentImage || currentImageGroup ? 'items-start' : 'items-start overflow-hidden'}`}
          style={!currentImage && !currentImageGroup ? { height: 'calc(100vh - 80px)' } : undefined}
        >
          {!isVideoRoute ? renderContent() : <VideosQualityDashboard
            video={videoHistory.find(video => video.video_id === currentVideoId)}
            onGoBack={() => navigate(`/projects/${currentProjectId}/threads/${currentThreadId}`)}
            isParentLoading={isLoading} />}
        </main>

        {/* Floating buttons - positioned fixed, outside main content flow */}

        {/* Active Jobs Indicator - bottom right floating tab */}
        {currentProjectId && (
          <ActiveJobsIndicator jobs={activeJobs} projectId={currentProjectId} thumbnailMap={thumbnailMap} />
        )}

        <BrandGuidelinesGateModal />

        {/* Floating Action Cluster - bottom right */}
        {currentProjectId && currentThreadId && !isVideoRoute && (
          <FloatingActionCluster
            onOpenChat={() => { setIsChatOpen(true); setIsFeedbackPanelExpanded(false); }}
            onOpenFeedback={() => { setIsFeedbackPanelExpanded(true); setIsChatOpen(false); }}
            onOpenGallery={handleOpenGallery}
            unreadFeedbackCount={comments.filter(c => c.user_id !== uid && !c.parent_comment_id).length}
            totalImageCount={totalImageCount}
            hasConversation={hasChatConversation}
            hasActiveJobs={activeJobs.length > 0}
            hasAccessToAiChat={hasAccessToAiChat}
            showGallery={editorMode === 'picture' && totalImageCount > 0}
            isChatOpen={isChatOpen}
            isFeedbackOpen={isFeedbackPanelExpanded}
          />
        )}

        {currentProjectId && currentThreadId && !isVideoRoute && (
          <ChatWidget
            hasAccessToAiChat={hasAccessToAiChat}
            isOpen={isChatOpen}
            onOpen={() => setIsChatOpen(true)}
            onClose={() => setIsChatOpen(false)}
            onCancelAskAI={handleCancelAskAI}
            onConversationChange={setHasChatConversation}
          />
        )}

        {/* Image Gallery Modal */}
        {isImageGalleryOpen && (
          <ImageGalleryModal
            imageGroups={imageGroups}
            currentHistoryIndex={historyIndex}
            onClose={handleCloseGallery}
            onNavigateToImage={handleNavigateToImage}
            onDownload={handleGalleryDownload}
            onToggleStar={handleToggleStar}
            onDeletePicture={handleDeletePicture}
          />
        )}
      </div>
    );
};

export default LandingPageView;
